import type { IndexDef } from '@/api/types/readiness'
import type { MigrationScope } from '@/lib/migration/scope'
import type { PlanData, PlanSystem } from '@/lib/migration/usePlan'
import type { DiscoveryFinding, MigrationItem, MigrationItemStatus, MigrationPlanVersion, PlanChecks, SystemAnnex, WaveDef } from '@/api/types/migration'
import { ANNEX_FIELD_COUNT, annexCompleteness, awaitingRescan, assetIdsOf, canClose, DAY_MS, isBlocked, partACompleteness, planCompleteness, statusSummary } from '@/lib/migration/engine'

// ── MigrationSummary (I3 §7.2, Note 8) ──────────────────────────────────────
// One object holding every hub tile, the overview card and the sidebar chip.
// Plan numbers come from the backend's plan (PlanData); tracker numbers from
// the browser store (MigrationScope). Each number is derived once here.

export interface AnnexRow {
  annex: SystemAnnex
  system: PlanSystem | null
  assets: number
  items: number
  completeness: { answered: number; total: number; open: string[] }
}

export interface MigrationSummary {
  cii_count: number
  // CII systems with at least one in-scope annex linked to them: never above
  // cii_count, however many copies or untitled annexes exist.
  cii_covered: number
  // Every in-scope annex, CII-linked or not.
  annex_count: number
  // Annexes a person archived out of Part B.
  archived_count: number
  completeness_pct: number
  // Part B's share of completeness_pct: annex scores over max(CII systems, annexes).
  part_b_pct: number
  draft_label: string // e.g. "v2"
  latest_version: MigrationPlanVersion | null
  submitted: boolean // the live version was marked as submitted
  due: { date: string; days: number } | null
  part_a: ReturnType<typeof partACompleteness>
  annex_rows: AnnexRow[]
  missing_systems: PlanSystem[]
  items: {
    total: number
    open: number
    by_status: Record<MigrationItemStatus, number>
    awaiting_rescan: number
    action_rows: MigrationItem[]
  }
  waves: Array<{ wave: WaveDef; count: number; blocked: number; flag: string | null }>
  unscheduled: DiscoveryFinding[]
  sources: { discovery_last_scan: string | null; playbook_notes: number; vendors_with_roadmap: number; vendors_total: number; readiness_fields: number; manual_fields: number }
  checks: PlanChecks
}

export const STATUS_TOKEN: Record<MigrationItemStatus, string> = {
  not_started: 'var(--rd-status-neutral)',
  planned: 'var(--rd-tint-l2-text)',
  in_progress: 'var(--rd-status-info)',
  verified: 'var(--rd-status-warn)',
  closed: 'var(--rd-status-ok)',
}

const EMPTY_CHECKS: PlanChecks = { blocking: [], attention: [], completeness: 0 }

export function unassignedFindings(scope: MigrationScope | null): DiscoveryFinding[] {
  if (!scope) return []
  const assigned = new Set(scope.items.map((i) => i.created_from_finding_id).filter(Boolean))
  const covered = new Set(scope.item_assets.map((x) => x.asset_id))
  return (scope.facts?.findings ?? []).filter((f) => !assigned.has(f.id) && !f.asset_ids.every((id) => covered.has(id)))
}

// Annexes the plan counts and exports: a sync parks an annex whose system is
// no longer CII, and a person can archive one; both stay visible but score nothing.
export const inScope = (annexes: SystemAnnex[]) => annexes.filter((a) => !a.no_longer_cii && !a.archived_at)
export const archivedAnnexes = (annexes: SystemAnnex[]) => annexes.filter((a) => !!a.archived_at)

// The CII systems that have an in-scope annex linked to them.
export const coveredCii = (cii: PlanSystem[], annexes: SystemAnnex[]) => cii.filter((s) => annexes.some((a) => a.system_id === s.id)).length

export type AnnexProgress = 'not_started' | 'in_progress' | 'complete'

// What the status column shows: complete once a person marks it, otherwise
// in progress as soon as any scored field is answered (by hand or by a sync).
export function annexProgress(annex: SystemAnnex, answered: number): AnnexProgress {
  if (annex.status === 'complete') return 'complete'
  return answered > 0 || annex.status === 'in_progress' ? 'in_progress' : 'not_started'
}

// Part B's half of plan completeness, as a percentage (engine planCompleteness).
function partBPct(annexes: SystemAnnex[], ciiCount: number): number {
  const denominator = Math.max(ciiCount, annexes.length)
  if (!denominator) return 0
  const sum = annexes.reduce((s, a) => s + annexCompleteness(a, a.blocks).answered / ANNEX_FIELD_COUNT, 0)
  return Math.round((sum / denominator) * 100)
}

export function buildSummary(index: IndexDef, data: PlanData, scope: MigrationScope | null, now = new Date()): MigrationSummary | null {
  const plan = data.plan
  if (!plan) return null
  const facts = scope?.facts ?? null
  const items = scope?.items ?? []
  // A system whose annex was archived is out of scope even while Discovery flags it CII.
  const archivedSystems = new Set(archivedAnnexes(data.annexes).map((a) => a.system_id).filter((x): x is string => !!x))
  const cii = data.systems.filter((s) => s.cii && !archivedSystems.has(s.id))
  const annexes = inScope(data.annexes)
  const latest = data.versions[data.versions.length - 1] ?? null
  const submitted = plan.plan_state.status === 'submitted'
  const dueDate = plan.plan_state.submission_date ?? index.submission?.deadline ?? index.milestone?.date ?? null
  const by = statusSummary(items)
  const open = items.filter((i) => i.status !== 'closed')
  const awaiting = scope ? items.filter((i) => awaitingRescan(i, assetIdsOf(i.id, scope.item_assets), scope.item_events, scope.rescans)) : []
  // Rows needing action: re-scan pending first, then by target date.
  const action = [...open].sort((a, b) => {
    const ra = awaiting.includes(a) ? 0 : 1
    const rb = awaiting.includes(b) ? 0 : 1
    if (ra !== rb) return ra - rb
    return (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999')
  })
  const waves = scope
    ? (facts?.waves ?? []).map((w) => {
        const inWave = items.filter((i) => i.wave_id === w.id)
        const blocked = inWave.filter((i) => i.status !== 'closed' && isBlocked(i, items, scope.item_deps)).length
        const gated = inWave.filter((i) => i.vendor_gated_year && i.status !== 'closed').length
        const ready = inWave.filter((i) => i.status === 'verified' && canClose(i, assetIdsOf(i.id, scope.item_assets), scope.item_events, scope.rescans).ok).length
        const flag = blocked ? `${blocked} blocked by a dependency` : ready ? `${ready} ready to close` : gated ? `${gated} vendor-gated` : inWave.every((i) => i.status === 'closed') && inWave.length ? 'all closed' : null
        return { wave: w, count: inWave.length, blocked, flag }
      })
    : []
  let readinessFields = 0, manualFields = 0
  const countMeta = (m: Record<string, { source: string; overridden: boolean }>) => Object.values(m).forEach((x) => (x.source === 'readiness' && !x.overridden ? readinessFields++ : x.source === 'manual' || x.overridden ? manualFields++ : null))
  countMeta(plan.field_meta)
  annexes.forEach((a) => {
    countMeta(a.field_meta)
    a.blocks.forEach((b) => countMeta(b.field_meta))
  })
  const lastScan = data.systems.map((s) => s.last_scan_at).filter((x): x is string => !!x).sort().pop() ?? null
  const checks = data.checks ?? EMPTY_CHECKS
  return {
    cii_count: cii.length,
    cii_covered: coveredCii(cii, annexes),
    annex_count: annexes.length,
    archived_count: archivedAnnexes(data.annexes).length,
    completeness_pct: data.checks?.completeness ?? planCompleteness(plan, annexes, cii.length),
    part_b_pct: partBPct(annexes, cii.length),
    draft_label: `v${plan.plan_state.n}`,
    latest_version: latest,
    submitted,
    due: dueDate ? { date: dueDate, days: Math.ceil((new Date(dueDate).getTime() - now.getTime()) / DAY_MS) } : null,
    part_a: partACompleteness(plan),
    annex_rows: annexes.map((a) => ({
      annex: a,
      system: data.systems.find((s) => s.id === a.system_id) ?? null,
      assets: a.blocks.length,
      items: items.filter((i) => a.system_id && i.system_id === a.system_id).length,
      completeness: annexCompleteness(a, a.blocks),
    })),
    missing_systems: cii.filter((s) => !annexes.some((a) => a.system_id === s.id)),
    items: { total: items.length, open: open.length, by_status: by, awaiting_rescan: awaiting.length, action_rows: action.slice(0, 3) },
    waves,
    unscheduled: unassignedFindings(scope),
    sources: {
      discovery_last_scan: lastScan,
      playbook_notes: facts?.playbook_notes.length ?? 0,
      vendors_with_roadmap: (facts?.vendors ?? []).filter((v) => v.roadmap).length,
      vendors_total: facts?.vendors.length ?? 0,
      readiness_fields: readinessFields,
      manual_fields: manualFields,
    },
    checks,
  }
}
